import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { emit } from '@/lib/analytics';
import { UnexpectedError, UserBlockingError } from '@hookmyapp/observability/errors';
import { captureFrontendError } from '@/lib/sentry';
import { isValidPublicId } from '@/lib/public-id';
import { writePopupContext } from '@/lib/meta-popup-context';
import { connectingPath, isInviteConnectPending } from '@/lib/invite-connect';

/**
 * /meta/callback/instagram — public landing page for Instagram Login OAuth.
 *
 * Sibling of /meta/callback/whatsapp (MetaCallbackPage) and
 * /meta/callback/instagram-fb (FacebookLoginCallbackPage). The URL itself
 * tells the SPA which flow this is — no ?channelType querystring needed
 * (Meta rejects redirect_uri with query discriminators on strict-match).
 *
 * Mirrors MetaCallbackPage's three-path reconcile (postMessage → opener,
 * localStorage relay for broken-opener popups, direct-nav fallback for
 * mobile). The only difference is channelType is hardcoded to
 * 'instagram_login' because we're on the Instagram callback URL — the SPA
 * already knows.
 *
 * State shape: `${nonce}.${wsPublicId}` per CONTEXT.md (cf34ade + 14f60db).
 */
export function MetaCallbackInstagramPage() {
  const navigate = useNavigate();
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    const channelType = 'instagram_login'; // implicit from URL

    // AIT-529 / Codex review round 14 (P2): an invited client must not be
    // dropped into the workspace flow when the OAuth return fails — they have
    // no workspace to land in, and the pending invite flag would still be set
    // for a later ordinary connect to trip over.
    const failureDestination = isInviteConnectPending() ? '/invite/connect' : '/';

    const oauthError = params.get('error');
    if (oauthError) {
      emit('meta_oauth_returned', {
        success: false,
        reason: oauthError,
        error_reason: params.get('error_reason') ?? undefined,
        channelType,
      });
      navigate(failureDestination, { replace: true });
      return;
    }

    if (!code) {
      captureFrontendError(
        new UnexpectedError({
          message: 'Instagram callback reached with neither code nor error param',
          code: 'IG_CALLBACK_NO_CODE_NO_ERROR',
        }),
      );
      emit('meta_oauth_returned', {
        success: false,
        reason: 'no_code',
        channelType,
      });
      navigate(failureDestination, { replace: true });
      return;
    }

    const toastThenNav = (msg: string, to: string) => {
      setTimeout(() => {
        toast.error(msg);
        setTimeout(() => navigate(to, { replace: true }), 0);
      }, 0);
    };

    if (!returnedState) {
      captureFrontendError(
        new UserBlockingError({
          message: 'Instagram callback received a code but no state',
          code: 'IG_CALLBACK_STATE_MISSING',
        }),
      );
      emit('meta_oauth_returned', { success: false, channelType });
      toastThenNav(
        "We couldn't connect Instagram right now. Please try again in a moment.",
        failureDestination,
      );
      return;
    }

    const parts = returnedState.split('.');
    if (parts.length !== 2) {
      captureFrontendError(
        new UserBlockingError({
          message: 'Instagram callback state is malformed (expected nonce.wsPublicId)',
          code: 'IG_CALLBACK_STATE_MALFORMED',
        }),
      );
      emit('meta_oauth_returned', { success: false, channelType });
      toastThenNav(
        "We couldn't connect Instagram right now. Please try again in a moment.",
        failureDestination,
      );
      return;
    }
    const [, wsPublicId] = parts;

    if (!isValidPublicId(wsPublicId, 'ws')) {
      captureFrontendError(
        new UserBlockingError({
          message: 'Instagram callback state carried an invalid wsPublicId',
          code: 'IG_CALLBACK_STATE_INVALID_WS',
        }),
      );
      emit('meta_oauth_returned', { success: false, channelType });
      toastThenNav(
        "We couldn't connect Instagram right now. Please try again in a moment.",
        failureDestination,
      );
      return;
    }

    emit('meta_oauth_returned', { success: true, channelType });

    // Path 1 — popup with intact opener
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'META_SIGNUP_CODE',
          code,
          wsPublicId,
          state: returnedState,
          channelType,
        },
        window.location.origin,
      );
      window.close();
      return;
    }

    // Path 2 — popup with broken opener (tunnel interstitial)
    const isProbablyPopup = window.history.length <= 2;
    if (isProbablyPopup) {
      localStorage.setItem(
        'meta_signup_code',
        JSON.stringify({ code, wsPublicId, state: returnedState, channelType }),
      );
      window.close();
      return;
    }

    // Path 3 — direct nav (mobile or pasted URL)
    writePopupContext(returnedState, {
      source: 'mobile_redirect',
      payload: null,
    });
    navigate(
      connectingPath(
        wsPublicId,
        `code=${encodeURIComponent(code)}&state=${encodeURIComponent(returnedState)}&channelType=${encodeURIComponent(channelType)}`,
      ),
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return null; // pure transit page
}
